import { type JSX } from 'react';
import { useAccount } from 'wagmi';
import Card from '@/shared/components/flip-ui-kit/Card';
import PageTab, { type TabOption } from '@/shared/components/PageTab';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import useAuction from '@/shared/hooks/useAuction';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useQueryParam from '@/shared/hooks/useQueryParam';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import GavelIcon from '@/shared/icons/flip-ui-kit/large/Gavel';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/large/MoneyBag';
import NetworkIcon from '@/shared/icons/flip-ui-kit/large/Network';
import NodeIcon from '@/shared/icons/flip-ui-kit/large/Node';
import UsersIcon from '@/shared/icons/flip-ui-kit/large/Users';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import OperatorContent from 'packages/stake/components/v2/operator/OperatorContent';
import { legacyWallets } from 'packages/stake/utils/consts';
import { Layout } from '../../components/v2/Layout';
import Nodes from '../../components/v2/legacy/Nodes';
import { DelegateBannerCard } from '../../components/v2/operator/DelegateBannerCard';
import { OperatorsTable } from '../../components/v2/operator/OperatorsTable';
import { SetupValidatorBannerCard } from '../../components/v2/operator/SetupValidatorBannerCard';
import { getAuthorityTotalRewardsQuery } from '../../graphql/explorer/rewards';
import useOperatorAccount from '../../hooks/useOperatorAccount';

export default function Operators(): JSX.Element | null {
  const [selectedTab, setSelectedTab] = useQueryParam<'overview' | 'my-operation' | 'legacy-nodes'>(
    {
      name: 'tab',
      defaultValue: 'overview',
      parse: (param: string) => (param === 'overview' ? 'overview' : 'my-operation'),
    },
  );

  const operator = useOperatorAccount();

  const { auction, isLoading: isAuctionsLoading } = useAuction({
    pollInterval: TABLE_POLL_INTERVAL,
  });

  const { isLoading: rewardsLoading, data: rewards } = useGqlQuery(getAuthorityTotalRewardsQuery, {
    refetchInterval: TABLE_POLL_INTERVAL,
    context: { clientName: 'processor' },
  });

  const rewardTokenAmount =
    rewards?.all?.aggregates?.sum?.reward != null
      ? TokenAmount.fromAsset(rewards.all.aggregates.sum.reward, 'Flip')
      : undefined;

  const tabs: TabOption<typeof selectedTab>[] = [
    {
      Icon: NetworkIcon,
      iconClassName: 'group-data-active:text-cf-green-1',
      title: 'Overview',
      value: 'overview',
    },
    {
      Icon: UsersIcon,
      iconClassName: 'group-data-active:text-cf-green-1',
      title: 'My Operation',
      value: 'my-operation',
    },
  ];

  const { address } = useAccount();

  if (address && legacyWallets.has(address.toLowerCase())) {
    tabs.push({
      Icon: NodeIcon,
      iconClassName: 'group-data-active:text-cf-green-1',
      title: 'My Nodes (legacy)',
      value: 'legacy-nodes' as const,
    });
  }

  return (
    <Layout title="Operators">
      <div className="flex flex-col gap-y-6">
        <PageTab selectedTab={selectedTab} onSelect={setSelectedTab} options={tabs} />

        {selectedTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <DelegateBannerCard />
              <SetupValidatorBannerCard idSs58={operator?.id} setSelectedTab={setSelectedTab} />
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card
                className="w-full"
                skeletonLineWidth={100}
                loading={isAuctionsLoading}
                icon={<CoinsIcon className="text-cf-light-2" />}
                title="Total Delegated"
                tooltip="The total amount of FLIP currently delegated to Operators across the network."
                footer={<span>{formatUsdValue(auction?.delegation.valueUsd)}</span>}
              >
                {auction?.delegation.amount?.toFormatted('numeral')} {FLIP_SYMBOL}
              </Card>
              <Card
                className="w-full"
                skeletonLineWidth={100}
                loading={rewardsLoading}
                icon={<MoneyBagIcon className="text-cf-light-2" />}
                title="Total Rewards"
                tooltip="The total amount of rewards earned by Operators and Delegators."
                footer={
                  <span>
                    {formatUsdValue(rewards?.all?.aggregates?.sum?.rewardValueUsd, false)}
                  </span>
                }
              >
                {rewardTokenAmount?.toFormatted('numeral')} {FLIP_SYMBOL}
              </Card>
              <Card
                className="w-full"
                skeletonLineWidth={100}
                loading={isAuctionsLoading}
                icon={<GavelIcon className="text-cf-light-2" />}
                title="Minimum Active Bid"
                tooltip="MAB is the lowest winning bid if the auction were to resolve now."
                tooltipLink="https://docs.chainflip.io/validators/validator-auctions-bonds-and-rewards"
                footer={<span>{formatUsdValue(auction?.minActiveBidUSDC, false)}</span>}
              >
                {auction?.minActiveBid?.toFormatted('numeral')} {FLIP_SYMBOL}
              </Card>
            </div>
            <OperatorsTable />
          </>
        )}
        {selectedTab === 'my-operation' && <OperatorContent />}
        {selectedTab === 'legacy-nodes' && <Nodes address={address!} />}
      </div>
    </Layout>
  );
}
